import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  JOKER_POOL,
  CONSUMABLE_POOL,
  HAND_COMBOS,
  describeJokerEffect,
  describeConsumableEffect,
  JokerDefinition,
  ConsumableDefinition,
  JokerRarity,
  HandComboInfo,
  getJokerById,
  getEvolutionChain,
  getEvolutionTierColor,
  getEvolutionTierDisplayName,
  getShopEligibleJokers,
} from '@solante/shared';
import { soundManager } from '../../utils/sounds';

const rarityStyles: Record<
  string,
  {
    badge: string;
    className: string;
    headerClass: string;
  }
> = {
  common: {
    badge: '\u25cf',
    className: 'border-white/20 bg-white/5',
    headerClass: 'text-white/80',
  },
  uncommon: {
    badge: '\u25c6',
    className: 'border-emerald-400/40 bg-emerald-500/10',
    headerClass: 'text-emerald-300',
  },
  rare: {
    badge: '\u2736',
    className: 'border-violet-400/40 bg-violet-500/10',
    headerClass: 'text-violet-300',
  },
  legendary: {
    badge: '\u2605',
    className: 'border-amber-400/60 bg-amber-500/15',
    headerClass: 'text-amber-300',
  },
};

const rarityOrder: Record<JokerRarity, number> = {
  legendary: 0,
  rare: 1,
  uncommon: 2,
  common: 3,
};

type CategoryFilter = 'all' | 'combos' | 'jokers' | 'evolutions' | 'consumables';
type RarityFilter = 'all' | JokerRarity;

const JokerCard: React.FC<{ joker: JokerDefinition; showEvolution?: boolean }> = ({
  joker,
  showEvolution = true,
}) => {
  const style = rarityStyles[joker.rarity] || rarityStyles.common;
  const evolutionTier = joker.evolution?.tier;
  const tierColor = evolutionTier ? getEvolutionTierColor(evolutionTier) : undefined;
  const tierName = evolutionTier ? getEvolutionTierDisplayName(evolutionTier) : undefined;
  const isEvolved = evolutionTier === 'awakened' || evolutionTier === 'ascended';
  const canEvolve = joker.evolution?.nextEvolution;
  const evolvedJoker = canEvolve ? getJokerById(canEvolve.evolvesTo) : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      className={`rounded-xl border p-4 ${style.className} ${isEvolved ? 'ring-1 ring-inset' : ''} h-full flex flex-col`}
      style={isEvolved ? ({ '--tw-ring-color': tierColor } as React.CSSProperties) : undefined}
    >
      {/* Header row with rarity badge, name, and cost */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-lg leading-none ${style.headerClass}`}>{style.badge}</span>
          {isEvolved && (
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase shrink-0"
              style={{ backgroundColor: tierColor, color: '#000' }}
            >
              {tierName}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          {joker.cost > 0 ? (
            <span className="text-xs font-semibold text-amber-300">{joker.cost} chips</span>
          ) : (
            <span className="text-xs font-semibold text-violet-300">Evolution</span>
          )}
          <span className="text-[10px] uppercase tracking-wide text-white/40">{joker.rarity}</span>
        </div>
      </div>

      {/* Name - full width, no truncation */}
      <h3 className="font-display text-sm font-semibold text-white mb-2 leading-tight">
        {joker.name}
      </h3>

      {/* Description - full text, no line clamping */}
      <p className="text-xs text-white/60 leading-relaxed mb-3 flex-grow">{joker.description}</p>

      {/* Effects */}
      <div className="space-y-1.5 border-t border-white/10 pt-3">
        {joker.effects.map((effect, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-white/70">
            <span className="text-arcade-cyan shrink-0">+</span>
            <span className="leading-snug">{describeJokerEffect(effect)}</span>
          </div>
        ))}
      </div>

      {/* Evolution info */}
      {showEvolution && canEvolve && (
        <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-500/10 p-2.5">
          <p className="text-xs text-amber-300 font-semibold mb-1">⚡ Can Evolve</p>
          <p className="text-xs text-white/60 leading-snug">{canEvolve.condition.description}</p>
          {evolvedJoker && (
            <p className="text-xs text-white/80 mt-1.5">
              → <span className="font-semibold">{evolvedJoker.name}</span>
            </p>
          )}
        </div>
      )}

      {/* Tags */}
      {joker.tags.length > 0 && !joker.tags.includes('evolved') && (
        <div className="mt-3 flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
          {joker.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-white/50"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
};

const ConsumableCard: React.FC<{ consumable: ConsumableDefinition }> = ({ consumable }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-4 h-full flex flex-col"
    >
      {/* Header with icon and cost */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className="text-lg text-emerald-300 leading-none">{'\u2B21'}</span>
        <span className="text-xs font-semibold text-amber-300 shrink-0">
          {consumable.cost > 0 ? `${consumable.cost} chips` : 'Free'}
        </span>
      </div>

      {/* Name - full width */}
      <h3 className="font-display text-sm font-semibold text-white mb-2 leading-tight">
        {consumable.name}
      </h3>

      {/* Description - full text */}
      <p className="text-xs text-white/60 leading-relaxed mb-3 flex-grow">
        {consumable.description}
      </p>

      {/* Effects */}
      <div className="space-y-1.5 border-t border-white/10 pt-3">
        {consumable.effects.map((effect, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-white/70">
            <span className="text-emerald-400 shrink-0">+</span>
            <span className="leading-snug">{describeConsumableEffect(effect)}</span>
          </div>
        ))}
      </div>

      {/* Tags */}
      {consumable.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
          {consumable.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-white/50"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
};

const ComboCard: React.FC<{ combo: HandComboInfo; rank: number }> = ({ combo, rank }) => {
  const tierColor =
    rank <= 3
      ? 'border-amber-400/50 bg-amber-500/10'
      : rank <= 6
        ? 'border-violet-400/40 bg-violet-500/10'
        : 'border-white/20 bg-white/5';

  const tierText = rank <= 3 ? 'text-amber-300' : rank <= 6 ? 'text-violet-300' : 'text-white/70';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      className={`rounded-xl border p-4 ${tierColor} h-full flex flex-col`}
    >
      {/* Rank badge */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className={`font-display text-xl font-bold ${tierText}`}>#{rank}</span>
      </div>

      {/* Name - full width */}
      <h3 className="font-display text-sm font-semibold text-white mb-2 leading-tight">
        {combo.name}
      </h3>

      {/* Description - full text */}
      <p className="text-xs text-white/60 leading-relaxed mb-3 flex-grow">{combo.description}</p>

      {/* Scoring stats */}
      <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-3 mb-3">
        <div className="text-center">
          <p className="text-[10px] text-white/40 uppercase tracking-wide mb-1">Base</p>
          <p className="font-display text-sm text-arcade-cyan">{combo.baseScore}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-white/40 uppercase tracking-wide mb-1">Mult</p>
          <p className="font-display text-sm text-arcade-pink">x{combo.multiplier}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-white/40 uppercase tracking-wide mb-1">Total</p>
          <p className="font-display text-sm text-emerald-300">
            {Math.round(combo.baseScore * combo.multiplier)}
          </p>
        </div>
      </div>

      {/* Example */}
      <div className="rounded-lg bg-black/30 px-3 py-2">
        <span className="text-[10px] text-white/40 uppercase tracking-wide block mb-1">
          Example
        </span>
        <span className="font-mono text-xs text-white/70 leading-snug">{combo.example}</span>
      </div>
    </motion.div>
  );
};

const EvolutionChainCard: React.FC<{ baseJoker: JokerDefinition }> = ({ baseJoker }) => {
  const chain = getEvolutionChain(baseJoker.id);

  if (chain.length < 2) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.005 }}
      className="rounded-xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-violet-500/10 p-5"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
        <span className="text-sm font-bold text-amber-400">EVO</span>
        <div>
          <h3 className="font-display text-sm font-semibold text-white leading-tight">
            {baseJoker.name} Evolution Line
          </h3>
          <span className="text-xs text-white/40">{chain.length} forms</span>
        </div>
      </div>

      {/* Evolution chain */}
      <div className="space-y-3">
        {chain.map((joker, index) => {
          const tier = joker.evolution?.tier || 'base';
          const tierColor = getEvolutionTierColor(tier);
          const tierName = getEvolutionTierDisplayName(tier);
          const style = rarityStyles[joker.rarity] || rarityStyles.common;
          const nextEvolution = joker.evolution?.nextEvolution;
          const isLast = index === chain.length - 1;

          return (
            <div key={joker.id}>
              <div
                className={`rounded-lg border p-4 ${style.className}`}
                style={
                  tier !== 'base'
                    ? ({
                        '--tw-ring-color': tierColor,
                        borderColor: tierColor,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                {/* Tier and rarity header */}
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span
                    className="rounded px-2 py-0.5 text-[10px] font-bold uppercase"
                    style={{ backgroundColor: tierColor, color: tier === 'base' ? '#fff' : '#000' }}
                  >
                    {tierName}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-white/40">
                    {joker.rarity}
                  </span>
                </div>

                {/* Name */}
                <h4 className="font-display text-sm font-semibold text-white mb-2 leading-tight">
                  {joker.name}
                </h4>

                {/* Description */}
                <p className="text-xs text-white/60 leading-relaxed mb-3">{joker.description}</p>

                {/* Effects */}
                <div className="space-y-1.5 border-t border-white/10 pt-3">
                  {joker.effects.map((effect, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-white/70">
                      <span className="text-arcade-cyan shrink-0">+</span>
                      <span className="leading-snug">{describeJokerEffect(effect)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Evolution arrow and condition */}
              {!isLast && nextEvolution && (
                <div className="flex items-center gap-3 py-3 pl-4">
                  <span className="text-amber-400 text-lg">↓</span>
                  <span className="text-xs text-amber-300/80 italic leading-snug">
                    {nextEvolution.condition.description}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export const InfoPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [rarityFilter, setRarityFilter] = useState<RarityFilter>('all');

  // Get shop-eligible jokers (excludes evolved forms)
  const shopJokers = useMemo(() => getShopEligibleJokers(), []);

  // Get base jokers that can evolve
  const evolvableJokers = useMemo(() => {
    return JOKER_POOL.filter((j) => j.evolution?.tier === 'base' && j.evolution?.nextEvolution);
  }, []);

  const filteredJokers = useMemo(() => {
    const pool = categoryFilter === 'evolutions' ? evolvableJokers : shopJokers;
    return pool
      .filter((joker) => {
        const matchesSearch =
          joker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          joker.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRarity = rarityFilter === 'all' || joker.rarity === rarityFilter;
        return matchesSearch && matchesRarity;
      })
      .sort((a, b) => {
        const rarityDiff = rarityOrder[a.rarity] - rarityOrder[b.rarity];
        if (rarityDiff !== 0) return rarityDiff;
        return a.name.localeCompare(b.name);
      });
  }, [searchQuery, rarityFilter, categoryFilter, shopJokers, evolvableJokers]);

  const filteredConsumables = useMemo(() => {
    return CONSUMABLE_POOL.filter((consumable) => {
      const matchesSearch =
        consumable.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        consumable.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [searchQuery]);

  const jokersByRarity = useMemo(() => {
    const grouped: Record<JokerRarity, JokerDefinition[]> = {
      legendary: [],
      rare: [],
      uncommon: [],
      common: [],
    };
    filteredJokers.forEach((joker) => {
      grouped[joker.rarity].push(joker);
    });
    return grouped;
  }, [filteredJokers]);

  const showCombos = categoryFilter === 'all' || categoryFilter === 'combos';
  const showJokers = categoryFilter === 'all' || categoryFilter === 'jokers';
  const showEvolutions = categoryFilter === 'evolutions';
  const showConsumables = categoryFilter === 'all' || categoryFilter === 'consumables';

  const handleCategoryChange = (category: CategoryFilter) => {
    setCategoryFilter(category);
    soundManager.play('click');
  };

  const handleRarityChange = (rarity: RarityFilter) => {
    setRarityFilter(rarity);
    soundManager.play('click');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mx-auto max-w-6xl px-4 py-8"
    >
      <header className="mb-8 text-center">
        <h1 className="neon-text font-display text-3xl font-bold">Game Info</h1>
        <p className="mt-2 text-sm text-white/50">
          Reference guide for hand combos, jokers, and consumables
        </p>
      </header>

      <div className="mb-6 space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {(['all', 'combos', 'jokers', 'evolutions', 'consumables'] as const).map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`pixel-corners rounded-lg px-4 py-2 font-display text-xs uppercase tracking-wide transition-all ${
                  categoryFilter === category
                    ? category === 'evolutions'
                      ? 'bg-amber-400 text-black shadow-neon'
                      : 'bg-arcade-cyan text-black shadow-neon'
                    : 'border border-white/20 text-white/70 hover:border-arcade-cyan/50 hover:text-white'
                }`}
              >
                {category === 'all'
                  ? 'All'
                  : category === 'combos'
                    ? 'Combos'
                    : category === 'jokers'
                      ? 'Jokers'
                      : category === 'evolutions'
                        ? 'Evolutions'
                        : 'Consumables'}
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="Search by name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-white/20 bg-black/50 px-4 py-2 text-sm text-white placeholder-white/40 focus:border-arcade-cyan focus:outline-none sm:w-64"
          />
        </div>

        {(showJokers || showEvolutions) && (
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-white/50 self-center mr-2">Rarity:</span>
            {(['all', 'legendary', 'rare', 'uncommon', 'common'] as const).map((rarity) => {
              const style = rarity !== 'all' ? rarityStyles[rarity] : null;
              return (
                <button
                  key={rarity}
                  onClick={() => handleRarityChange(rarity)}
                  className={`rounded-full px-3 py-1 text-xs transition-all ${
                    rarityFilter === rarity
                      ? 'bg-white/20 text-white'
                      : 'border border-white/10 text-white/50 hover:border-white/30 hover:text-white/70'
                  }`}
                >
                  {style && <span className="mr-1">{style.badge}</span>}
                  {rarity === 'all' ? 'All' : rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showCombos && (
        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-arcade-cyan">
              Hand Combos ({HAND_COMBOS.length})
            </h2>
          </div>
          <p className="mb-4 text-xs text-white/50">
            Play cards to form these combos. Higher-ranked combos score more points!
          </p>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {HAND_COMBOS.map((combo, index) => (
              <ComboCard key={combo.id} combo={combo} rank={index + 1} />
            ))}
          </div>
        </section>
      )}

      {showJokers && (
        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-arcade-pink">
              Jokers ({filteredJokers.length})
            </h2>
          </div>

          {filteredJokers.length === 0 ? (
            <p className="text-sm text-white/50">No jokers match your search.</p>
          ) : (
            <div className="space-y-6">
              {(Object.entries(jokersByRarity) as [JokerRarity, JokerDefinition[]][])
                .filter(([, jokers]) => jokers.length > 0)
                .map(([rarity, jokers]) => {
                  const style = rarityStyles[rarity];
                  return (
                    <div key={rarity}>
                      <h3
                        className={`mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide ${style.headerClass}`}
                      >
                        <span>{style.badge}</span>
                        {rarity} ({jokers.length})
                      </h3>
                      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {jokers.map((joker) => (
                          <JokerCard key={joker.id} joker={joker} />
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </section>
      )}

      {showEvolutions && (
        <section className="mb-8">
          <div className="mb-4">
            <h2 className="font-display text-lg font-semibold text-amber-400">
              Joker Evolutions ({evolvableJokers.length} Evolution Lines)
            </h2>
            <p className="mt-2 text-xs text-white/50">
              Jokers can evolve during matches when you meet their evolution conditions. Each
              evolution makes the joker significantly stronger. Evolutions are session-based and
              reset between matches - everyone starts with base jokers!
            </p>
          </div>

          <div className="mb-6 rounded-xl border border-white/10 bg-white/5 p-4">
            <h3 className="font-display text-sm font-semibold text-white mb-3">
              How Evolution Works
            </h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex items-start gap-2">
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase shrink-0"
                  style={{ backgroundColor: getEvolutionTierColor('base'), color: '#fff' }}
                >
                  Base
                </span>
                <p className="text-xs text-white/60">
                  Starting form. Available in the shop during matches.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase shrink-0"
                  style={{ backgroundColor: getEvolutionTierColor('awakened'), color: '#000' }}
                >
                  Awakened
                </span>
                <p className="text-xs text-white/60">
                  ~40% stronger. Evolves when you meet the condition.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase shrink-0"
                  style={{ backgroundColor: getEvolutionTierColor('ascended'), color: '#000' }}
                >
                  Ascended
                </span>
                <p className="text-xs text-white/60">
                  Ultimate form. Exceptionally powerful effects.
                </p>
              </div>
            </div>
          </div>

          {filteredJokers.length === 0 ? (
            <p className="text-sm text-white/50">No evolvable jokers match your search.</p>
          ) : (
            <div className="grid gap-5 xl:grid-cols-2">
              {filteredJokers.map((joker) => (
                <EvolutionChainCard key={joker.id} baseJoker={joker} />
              ))}
            </div>
          )}
        </section>
      )}

      {showConsumables && (
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-emerald-300">
              Consumables ({filteredConsumables.length})
            </h2>
          </div>

          {filteredConsumables.length === 0 ? (
            <p className="text-sm text-white/50">No consumables match your search.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filteredConsumables.map((consumable) => (
                <ConsumableCard key={consumable.id} consumable={consumable} />
              ))}
            </div>
          )}
        </section>
      )}
    </motion.div>
  );
};
